import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  QButton,
  useCurrentUser,
  useToastProvider,
} from '@qualio/ui-components';

import { changeControlApi } from '../../../../../api/changeControl';
import { documentApi } from '../../../../../api/document';
import { QualioDocument } from '../../../../../api/model/document';
import { PeriodicReview } from '../../../../../api/model/periodicReview';
import {
  CCWorkflowLink,
  ChangeControlDTO,
  ChangeControlTemplateDTO,
} from '../../../../../types/DocumentCreate';
import { ButtonGroupContext } from '../../../Contexts/ButtonGroupContext';
import { DocumentOverviewContext } from '../../Context';
import { shouldRenderCreateNewDraftButton } from '../../RenderLogic';

import { getQRI } from '@qualio/search-qri';
import { ChangeControlSelectModal } from '../../ChangeControl/ChangeControlSelectModal';
import {
  useEmbeddedDocuments,
  useInvalidateEmbeddedDocuments,
} from '../../hooks/useEmbeddedDocuments';
import { EmbedWarningModal } from '../../Modals/EmbedWarningModal';
import { useQriServiceUrl } from '../../../../../hooks/useQriServiceUrl';

export const CreateNewDraftButton = ({
  periodicReview,
}: {
  periodicReview?: PeriodicReview;
}) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();
  const { qualioDocument } = useContext(DocumentOverviewContext);
  const { isDisabled, setDisabled } = useContext(ButtonGroupContext);
  const [isCreatingNewDraft, setIsCreatingNewDraft] = useState(false);
  const { showToast } = useToastProvider();
  const navigate = useNavigate();
  const qriServiceUrl = useQriServiceUrl();

  const [isFetchingCCOptions, setIsFetchingCCOptions] = useState(false);
  const [isEmbedWarningModalOpen, setIsEmbedWarningModalOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const documentQri = getQRI({
    companyId: qualioDocument.company_id.toString(),
    domain: 'documents',
    domainType: 'document',
    id: qualioDocument.document_matrix_id,
    qriBaseUrl: qriServiceUrl,
  });

  const invalidateEmbeddedDocuments =
    useInvalidateEmbeddedDocuments(documentQri);
  const { data, isLoading: isEmbeddedDocumentsLoading } = useEmbeddedDocuments(
    documentQri,
    true,
    qualioDocument.company_id,
  );
  const embeddedDocuments = data ?? [];

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setIsEmbedWarningModalOpen(false);
  };

  const createSelectedCC = async (
    newDoc: QualioDocument,
    selectedCCWorkflow: ChangeControlTemplateDTO,
  ): Promise<string> => {
    try {
      if (selectedCCWorkflow) {
        await changeControlApi.createChangeControl(
          newDoc,
          Number(selectedCCWorkflow.id),
          { sections: selectedCCWorkflow.sections } as ChangeControlDTO,
        );
      }
    } catch {
      return '&tab=change-control';
    }
    return '';
  };

  const createNewDraft = async (
    selectedCCWorkflow?: ChangeControlTemplateDTO | null,
  ) => {
    setIsModalOpen(false);
    setDisabled(true, setIsCreatingNewDraft);
    let newDoc: QualioDocument;
    try {
      newDoc = await documentApi.createDraftFromEffectiveDoc(
        qualioDocument.company_id,
        qualioDocument.document_matrix_id,
      );
    } catch (e: any) {
      const failureMessage =
        e.response?.data?.message ||
        t('documentActions.failedToCreateNewDraft');
      showToast({
        'data-cy': 'create-new-draft-error-toast',
        id: 'create-new-draft-error-toast',
        status: 'error',
        title: t('notifications.error'),
        description: failureMessage,
      });
      setDisabled(false, setIsCreatingNewDraft);
      return;
    }

    let ccTab = '';
    if (selectedCCWorkflow) {
      ccTab = await createSelectedCC(newDoc, selectedCCWorkflow);
    }

    const url = `/workspace/documents/${newDoc.id}?${ccTab}`;
    void navigate(url);
  };

  const fetchCCOptions = async (): Promise<ChangeControlTemplateDTO[]> => {
    const allCCTemplates: ChangeControlTemplateDTO[] = (
      (await changeControlApi.fetchChangeControlTemplates()) ?? []
    ).filter((tmpl: ChangeControlTemplateDTO) => tmpl.status !== 'archived');
    const linkedCCWorkflows = qualioDocument.template_id
      ? (
          await changeControlApi.fetchLinkedCCWorkflows(
            qualioDocument.company_id,
            qualioDocument.template_id,
          )
        ).filter((wf) => wf.status !== 'archived')
      : [];
    const systemTemplate = allCCTemplates.find(
      (tmpl) => tmpl.qualio_managed && tmpl.status === 'effective',
    );
    const systemOptions = systemTemplate ? [systemTemplate] : [];
    const linkedOptions = linkedCCWorkflows
      .filter((wf: CCWorkflowLink) => !wf.qualio_managed)
      .map((wf: CCWorkflowLink) =>
        allCCTemplates.find((t) => String(t.id) === String(wf.id)),
      )
      .filter(Boolean) as ChangeControlTemplateDTO[];
    return [...systemOptions, ...linkedOptions];
  };

  const openCCModalOrSkip = (options: ChangeControlTemplateDTO[]) => {
    if (options.length === 1) {
      void createNewDraft(options[0]);
    } else {
      setIsModalOpen(true);
    }
  };

  const fetchAndOpenCC = async () => {
    setIsFetchingCCOptions(true);
    try {
      const options = await fetchCCOptions();
      openCCModalOrSkip(options);
    } finally {
      setIsFetchingCCOptions(false);
    }
  };

  const handleClickCreateDraft = async () => {
    await invalidateEmbeddedDocuments();
    if (embeddedDocuments.length > 0) {
      setIsEmbedWarningModalOpen(true);
    } else {
      await fetchAndOpenCC();
    }
  };

  if (
    !shouldRenderCreateNewDraftButton(qualioDocument, currentUser) ||
    periodicReview
  ) {
    return null;
  }

  return (
    <>
      <QButton
        isDisabled={isDisabled}
        isLoading={
          isCreatingNewDraft ||
          isEmbeddedDocumentsLoading ||
          isFetchingCCOptions
        }
        variant="outline"
        onClick={handleClickCreateDraft}
        data-cy="doc-overview-create-new-draft-button"
      >
        {t('buttons.createNewDraft')}
      </QButton>

      {/* CC workflow picker modal */}
      {isModalOpen && (
        <ChangeControlSelectModal
          onConfirm={createNewDraft}
          onClose={handleCloseModal}
          title={t('createNewDraft.selectChangeControlTemplate')}
          confirmButtonText={t('buttons.createDraft')}
          canSelectLater
        />
      )}
      <EmbedWarningModal
        isOpen={isEmbedWarningModalOpen}
        handleCreate={() => {
          setIsEmbedWarningModalOpen(false);
          void fetchAndOpenCC();
        }}
        handleClose={handleCloseModal}
        embeddedDocuments={embeddedDocuments}
      />
    </>
  );
};
